import { axialHexDistance } from '../../../hexmap/coords';
import { getHexTileMapKey } from '../../../hexmap/model/keys';
import type { HexTileMapKey, HexTileType } from '../../../hexmap/model/types';
import { pathTo, type Reachable } from '../../../hexmap/pathfinding';
import { fromMilli, toMilli } from '../../fixed';
import { buildingLevel, levelStat } from '../../levels';
import {
  addHeading,
  buildsFor,
  forget,
  type Occupancy,
  ringOrder,
  stopKind,
} from '../../occupancy';
import { redirect } from '../../redirect';
import { holdsStock, stockCities } from '../../stock';
import { mintOrder, outsideTerritory } from '../../territory';
import type { Building, Cell, EntityId, PlayerId, SimContext, SimState, Unit } from '../../types';
import { BUILD_TASK } from '../../workTargets';
import { cityAt, gatherResource, tileResources, unloadCity, workerSlots } from '../economy';
import {
  buildingsAt,
  cityReach,
  cityTiles,
  homeCity,
  inWorkArea,
  isMoving,
  isPos,
  type OwnerCache,
  ownerCache,
  ownTerritory,
  RETRY_TICKS,
  reachFor,
  retryAt,
  type TickCache,
  takeSearch,
} from './cache';
import { goToShelter, retreat, SAFE_RADIUS, threatDistance } from './threat';

/** Work and haul automation for workers (see index.ts and `stepWorker`). */

/**
 * A worker leaves its work tile (or, back from hauling, the tile it hauled
 * from) only for work worth more than this many times as much (see
 * `pickWork`), so workers don't flip between two near-equal jobs.
 */
const SWITCH_MARGIN = 1.15;
/** A working worker looks for clearly better gathering work this often (ticks), in its own phase. */
const REVIEW_TICKS = 10;
/** A city's `workFocus` resource is worth this many times as much to its workers… */
const FOCUS_WEIGHT = 1.6;
/**
 * …while fewer than this share of the city's gathering auto workers gather it
 * (past it the focus resource is valued as when balanced, so a focus never
 * puts fewer workers on it than balanced would).
 */
const FOCUS_SHARE = 0.6;
/**
 * Within one resource, a provider's place (a farm, forestry or mine: safe,
 * never runs out, and what the player built for its workers) counts this many
 * times its `throughput` against wild tiles, so a wild tile wins it only when
 * clearly better. An L1 farm gathers at half the wild rate, so at 2.5 it beats
 * a full wild field the same distance from the city and one a few tiles
 * nearer. Only the choice of place uses it: resources are balanced on real
 * throughput (`pickWork`), so a farm doesn't pull workers off wood.
 */
const PROVIDER_PREFERENCE = 2.5;
/** Seconds per sim tick (the 100 ms fixed step), for `pacing.depositTicks`. */
const TICK_SECONDS = 0.1;

/**
 * The tile `owner`'s worker on `cell` works from: the own tile of an own
 * completed providing building whose extra footprint tile `cell` is (a grown
 * farm is worked and claimed from its own tile), else `cell` itself.
 */
function workCell(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  owner: PlayerId,
  cell: Cell
): Cell {
  for (const b of buildingsAt(state, cache).get(getHexTileMapKey(cell)) ?? []) {
    if (b.owner !== owner || b.buildProgress < 1) continue;
    if (ctx.rules.buildings[b.type]?.provides) return b.pos;
  }
  return cell;
}

/** `owner`'s completed providing building whose own tile is `cell`, if any. */
function providerAt(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  owner: PlayerId,
  cell: Cell,
  key: HexTileMapKey
): Building | undefined {
  return (buildingsAt(state, cache).get(key) ?? []).find(
    (b) =>
      b.owner === owner &&
      b.buildProgress >= 1 &&
      isPos(b, cell) &&
      ctx.rules.buildings[b.type]?.provides !== undefined
  );
}

/**
 * The own completed city whose territory holds `cell`, else `home`: where a
 * worker working `cell` counts and hauls to. A worker whose home has no free
 * work works for another city without moving home (see `stepWorker`).
 */
function workCity(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  cell: Cell,
  home: Building | undefined
): Building | undefined {
  const city = state.buildings[cache.derived.owners().get(getHexTileMapKey(cell)) ?? ''];
  return city && city.owner === unit.owner && holdsStock(ctx.rules, city) ? city : home;
}

/**
 * Resource `owner`'s worker would gather on `cell` (mirrors
 * economy.gatherResource). A farm is worked from its own tile: its extra
 * footprint tiles offer no work of their own (nor wild resources).
 */
function workResource(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  owner: PlayerId,
  cell: Cell
): string | null {
  const here = buildingsAt(state, cache).get(getHexTileMapKey(cell)) ?? [];
  for (const b of here) {
    if (b.owner !== owner || b.buildProgress < 1) continue;
    const provides = ctx.rules.buildings[b.type]?.provides;
    if (!provides) continue;
    // Outside its owner's territory it's idle: no work there.
    if (!isPos(b, cell) || outsideTerritory(state, ctx.rules, b, cache.derived.owners()))
      return null;
    return provides.resource;
  }
  if (here.length > 0) return null;
  const wild = tileResources(state, ctx, cell);
  return wild && wild.remaining > 0 ? wild.resourceId : null;
}

/** Work tiers, best first: a site to build, then gathering (ranked by `workValue`). */
const SITE = 0;
const GATHER = 1;

interface WorkTarget {
  tier: number;
  /** Resource the tile yields (or will, for a site); balances workers across resources. */
  res: string;
}

/**
 * What `unit`'s worker would do on `cell`: build the owner's unfinished site
 * there (if the unit's type builds it), or gather (workResource).
 */
function workTarget(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  cell: Cell
): WorkTarget | null {
  const here = buildingsAt(state, cache).get(getHexTileMapKey(cell)) ?? [];
  for (const b of here) {
    if (b.owner !== unit.owner || b.buildProgress >= 1) continue;
    const def = ctx.rules.buildings[b.type];
    if (def && !def.consumesBuilder && buildsFor(ctx.rules, unit.type, def.builtBy)) {
      return { tier: SITE, res: def.provides?.resource ?? b.type };
    }
  }
  const res = workResource(state, ctx, cache, unit.owner, cell);
  if (!res) return null;
  return { tier: GATHER, res };
}

/**
 * Resources per second `unit` brings city `city` from gathering on `cell`
 * (`key`), round trips included: a load over the time to fill it there
 * (economy.ts `gatherRate`: base rate × the unit's `gatherMultiplier` × a
 * farm's level `rate` × (1 + the city's `gatherBonus`)) plus the walk from
 * the tile to a spot beside the city and back and the unload
 * (`pacing.depositTicks`). A wild tile's load is at most what's left on it,
 * so a nearly drained tile is worth less. A gatherer without a
 * `carryCapacity` never walks: just its rate.
 */
function throughput(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  cell: Cell,
  key: HexTileMapKey,
  city: Building
): number {
  const def = ctx.rules.units[unit.type];
  if (!def) return 0;
  const { pacing } = ctx.rules;
  let rate = pacing.baseGatherRate * (def.gatherMultiplier ?? 1);
  let left = Number.POSITIVE_INFINITY;
  const provider = providerAt(state, ctx, cache, unit.owner, cell, key);
  const providerDef = provider && ctx.rules.buildings[provider.type];
  if (provider && providerDef) rate *= levelStat(providerDef, buildingLevel(provider), 'rate') ?? 1;
  else left = fromMilli(tileResources(state, ctx, cell)?.remaining ?? 0);
  const holder = state.buildings[cache.derived.owners().get(key) ?? ''];
  const holderDef = holder && ctx.rules.buildings[holder.type];
  if (holder && holderDef && holder.owner === unit.owner) {
    rate *= 1 + (levelStat(holderDef, buildingLevel(holder), 'gatherBonus') ?? 0);
  }
  if (def.carryCapacity === undefined) return rate;
  const load = Math.min(def.carryCapacity, left);
  if (load <= 0 || rate <= 0) return 0;
  // To the free tile beside the city (one step short of it) and back.
  const steps =
    cityReach(state, ctx, cache, city, def.terrain).dist.get(key) ??
    axialHexDistance(cell, city.pos);
  const walk = (2 * Math.max(0, steps - 1)) / (pacing.baseSpeed * def.speedMultiplier);
  return load / (load / rate + walk + pacing.depositTicks * TICK_SECONDS);
}

// ── Workers ─────────────────────────────────────────────────────────────────

/** How many of `owner`'s workers the work on `cell` takes: a providing building's slots, else 1. */
function capacityAt(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  owner: PlayerId,
  cell: Cell
): number {
  for (const b of buildingsAt(state, cache).get(getHexTileMapKey(cell)) ?? []) {
    if (b.owner === owner && b.buildProgress >= 1 && ctx.rules.buildings[b.type]?.provides) {
      return isPos(b, cell) ? workerSlots(ctx.rules, b) : 1;
    }
  }
  return 1;
}

/**
 * The tile a worker holds: the site it's building, a pinned worker's gather
 * target, the work tile it keeps while hauling, the tile it's heading for, or
 * the one it stands on.
 */
function claimCell(state: SimState, u: Unit): Cell | null {
  const site = u.order?.kind === 'build' ? state.buildings[u.order.buildingId]?.pos : undefined;
  if (site) return site;
  if (u.order?.kind === 'gather' && u.order.pin) return u.order.target;
  if (u.auto?.phase === 'haul') return u.auto.work ?? null;
  return u.auto?.target ?? (isMoving(u) ? null : u.pos);
}

interface WorkerCounts {
  /**
   * City id → resource → auto workers holding gathering work on it in that
   * city's territory (a worker's home, or the city it works for while home
   * has no free work: `workCity`).
   */
  byHome: Map<EntityId, Map<string, number>>;
  /** Auto worker id → where it's counted in `byHome`. */
  counted: Map<EntityId, { home: EntityId; res: string }>;
  /**
   * City id → task (a resource, or BUILD_TASK) → the workers on it in that
   * city's territory: its own (`pinned`, `auto`; what its `workTargets` are
   * measured against) and other cities' workers working there (`visiting`).
   */
  tasks: Map<EntityId, Map<string, TaskCount>>;
  /** Worker id → where it's counted in `tasks`. */
  tasked: Map<EntityId, { city: EntityId; task: string; kind: keyof TaskCount }>;
}

interface TaskCount {
  pinned: number;
  auto: number;
  visiting: number;
}

/**
 * Counts `unit` under `task` in `tasks`, once: for the city it works for
 * (`cityId`, else its home), as its own worker only while that is its home.
 */
function countTask(
  counts: WorkerCounts,
  unit: Unit,
  task: string,
  pinned: boolean,
  cityId?: EntityId
): void {
  const city = cityId ?? unit.homeCityId;
  if (!city || counts.tasked.has(unit.id)) return;
  const kind = city !== unit.homeCityId ? 'visiting' : pinned ? 'pinned' : 'auto';
  counts.tasked.set(unit.id, { city, task, kind });
  const byTask = counts.tasks.get(city) ?? new Map<string, TaskCount>();
  const n = byTask.get(task) ?? { pinned: 0, auto: 0, visiting: 0 };
  n[kind]++;
  byTask.set(task, n);
  counts.tasks.set(city, byTask);
}

/** Takes `unit` back out of `tasks` (see `countTask`). */
function uncountTask(counts: WorkerCounts, unit: Unit): void {
  const entry = counts.tasked.get(unit.id);
  if (!entry) return;
  counts.tasked.delete(unit.id);
  const n = counts.tasks.get(entry.city)?.get(entry.task);
  if (n) n[entry.kind] = Math.max(0, n[entry.kind] - 1);
}

/** Counts `unit` (an auto worker) under city `cityId` (else its home) and `res` in `byHome`, once. */
function countWorker(counts: WorkerCounts, unit: Unit, res: string, cityId?: EntityId): void {
  const home = cityId ?? unit.homeCityId;
  if (!unit.auto || !home || counts.counted.has(unit.id)) return;
  counts.counted.set(unit.id, { home, res });
  const byRes = counts.byHome.get(home) ?? new Map<string, number>();
  byRes.set(res, (byRes.get(res) ?? 0) + 1);
  counts.byHome.set(home, byRes);
}

/** Takes `unit` back out of `byHome` (see `countWorker`). */
function uncountWorker(counts: WorkerCounts, unit: Unit): void {
  const entry = counts.counted.get(unit.id);
  if (!entry) return;
  counts.counted.delete(unit.id);
  const byRes = counts.byHome.get(entry.home);
  if (byRes) byRes.set(entry.res, Math.max(0, (byRes.get(entry.res) ?? 0) - 1));
}

/**
 * Every worker of `owner` holding work, counted per home city and task
 * (pinned and auto alike; sites are BUILD_TASK) in `counts.tasks`.
 */
function workerClaims(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  owner: PlayerId,
  oc: OwnerCache
): { claims: Map<HexTileMapKey, EntityId[]>; counts: WorkerCounts } {
  if (!oc.claims || !oc.workerCounts || !oc.counted || !oc.taskCounts || !oc.tasked) {
    const claims = new Map<HexTileMapKey, EntityId[]>();
    const counts: WorkerCounts = {
      byHome: new Map(),
      counted: new Map(),
      tasks: new Map(),
      tasked: new Map(),
    };
    // Pinned workers first: their slots are theirs, whoever is listed earlier.
    for (const pinned of [true, false]) {
      for (const u of Object.values(state.units)) {
        if (u.owner !== owner || isPinned(u) !== pinned) continue;
        if (!ctx.rules.units[u.type]?.actions.includes('gather')) continue;
        const at = claimCell(state, u);
        if (!at) continue;
        // A gatherer on a grown farm's extra tile holds one of its slots.
        const cell = workCell(state, ctx, cache, owner, at);
        const key = getHexTileMapKey(cell);
        const holders = claims.get(key) ?? [];
        if (holders.length >= capacityAt(state, ctx, cache, owner, cell)) continue;
        const work = workTarget(state, ctx, cache, u, cell);
        if (!work) continue;
        holders.push(u.id);
        claims.set(key, holders);
        if (work.tier === GATHER) {
          countWorker(counts, u, work.res, workCity(state, ctx, cache, u, cell, undefined)?.id);
        }
        countTask(
          counts,
          u,
          work.tier === GATHER ? work.res : BUILD_TASK,
          pinned,
          workCity(state, ctx, cache, u, cell, undefined)?.id
        );
      }
    }
    oc.claims = claims;
    oc.workerCounts = counts.byHome;
    oc.counted = counts.counted;
    oc.taskCounts = counts.tasks;
    oc.tasked = counts.tasked;
  }
  return {
    claims: oc.claims,
    counts: {
      byHome: oc.workerCounts,
      counted: oc.counted,
      tasks: oc.taskCounts,
      tasked: oc.tasked,
    },
  };
}

/** Whether `unit` holds or can take a place working `cell`. */
function freeFor(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  claims: Map<HexTileMapKey, EntityId[]>,
  unit: Unit,
  cell: Cell
): boolean {
  const holders = claims.get(getHexTileMapKey(cell));
  if (!holders || holders.includes(unit.id)) return true;
  return holders.length < capacityAt(state, ctx, cache, unit.owner, cell);
}

function claim(claims: Map<HexTileMapKey, EntityId[]>, key: HexTileMapKey, unitId: EntityId): void {
  const holders = claims.get(key);
  if (!holders) claims.set(key, [unitId]);
  else if (!holders.includes(unitId)) holders.push(unitId);
}

function release(unit: Unit, claims: Map<HexTileMapKey, EntityId[]>, counts: WorkerCounts): void {
  for (const [key, holders] of claims) {
    const i = holders.indexOf(unit.id);
    if (i < 0) continue;
    holders.splice(i, 1);
    if (holders.length === 0) claims.delete(key);
  }
  uncountWorker(counts, unit);
  uncountTask(counts, unit);
}

/**
 * Whether the worker's home territory offers it free, explored work of a
 * better tier than `tier` (a site, for a gathering worker).
 */
function betterWorkWaiting(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  oc: OwnerCache,
  claims: Map<HexTileMapKey, EntityId[]>,
  tier: number,
  home: Building,
  counts: WorkerCounts
): boolean {
  // A met build target takes no more builders.
  if (!buildOpen(home, counts)) return false;
  const built = buildingsAt(state, cache);
  for (const cell of cityTiles(cache, home.id)) {
    const key = getHexTileMapKey(cell);
    // A site always stands on a built-on tile.
    if (!built.has(key)) continue;
    if (!oc.explored(key) || !freeFor(state, ctx, cache, claims, unit, cell)) continue;
    const work = workTarget(state, ctx, cache, unit, cell);
    if (work && work.tier < tier) return true;
  }
  return false;
}

/**
 * Where `unit` can unload at city `city`: the free open tile next to it
 * nearest by path (then ring order), else (all taken) inside the city.
 * Undefined when neither is reachable.
 */
function unloadSpot(
  ctx: SimContext,
  occ: Occupancy,
  unit: Unit,
  city: Building,
  reach: Reachable
): Cell | undefined {
  let best: Cell | undefined;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const cell of ringOrder(city.pos, 1, 1)) {
    const dist = reach.dist.get(getHexTileMapKey(cell));
    if (dist === undefined || dist >= bestDist) continue;
    if (stopKind(ctx.rules, occ, unit, cell, true) !== 'open') continue;
    best = cell;
    bestDist = dist;
  }
  if (best) return best;
  return reach.dist.has(getHexTileMapKey(city.pos)) &&
    stopKind(ctx.rules, occ, unit, city.pos, true) === 'inside'
    ? city.pos
    : undefined;
}

/**
 * Where `unit` unloads: a spot at its `home` city when reachable, else at the
 * own completed city nearest by path, with the search that reaches it.
 */
function haulSpot(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  home: Building | undefined
): { dest: Cell; reach: Reachable } | undefined {
  const occ = cache.derived.occupancy();
  // Home first, searching only round it when the worker is there.
  const area = home && inWorkArea(ctx, home, unit.pos) ? home : undefined;
  let reach = reachFor(state, ctx, cache, unit, terrain, area);
  let dest = home ? unloadSpot(ctx, occ, unit, home, reach) : undefined;
  if (!dest) {
    if (area) reach = reachFor(state, ctx, cache, unit, terrain);
    const cities = new Map(
      stockCities(state, ctx.rules, unit.owner).map((c) => [getHexTileMapKey(c.pos), c])
    );
    // Breadth-first order: the first city found is the nearest.
    for (const cell of reach.order) {
      const city = cities.get(getHexTileMapKey(cell));
      dest = city && unloadSpot(ctx, occ, unit, city, reach);
      if (dest) break;
    }
  }
  return dest ? { dest, reach } : undefined;
}

/**
 * Sends `unit` to unload its carry next to (or, when that's all taken,
 * inside) its home city when reachable, else the own completed city nearest
 * by path. `work` is the tile it keeps claimed and comes back to.
 */
function startHaul(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  terrain: readonly HexTileType[],
  home: Building | undefined,
  work?: Cell
): void {
  const auto = unit.auto;
  if (!auto) return;
  auto.phase = 'haul';
  if (work) auto.work = { q: work.q, r: work.r };
  else delete auto.work;
  const found = haulSpot(state, ctx, cache, unit, terrain, home);
  if (!found) {
    delete auto.target;
    redirect(state, unit, [unit.pos]);
    auto.retryTick = retryAt(state, unit, RETRY_TICKS);
    return;
  }
  const occ = cache.derived.occupancy();
  redirect(state, unit, pathTo(found.reach, found.dest));
  forget(occ, unit);
  addHeading(occ, unit, found.dest);
  auto.target = { q: found.dest.q, r: found.dest.r };
  delete auto.retryTick;
}

/** A place to work found by stepWorker's search. */
interface WorkChoice {
  cell: Cell;
  res: string;
  tier: number;
  /** Gathering: `throughput` (× SWITCH_MARGIN on the tile the worker holds or hauled from); 0 for a site. */
  score: number;
  /** `score`, × PROVIDER_PREFERENCE on a provider's place: picks the place within a resource. */
  pick: number;
  /** Walking distance, for ties: from the worker, or (a review) from its home city. */
  near: number;
}

/** A city's best work for a worker: its nearest site and its best place per resource. */
interface CityWork {
  site?: WorkChoice;
  gather: Map<string, WorkChoice>;
}

/**
 * What a gathering place is worth to a worker of a city: its throughput
 * shared with the city's `on` other auto workers on that resource (so,
 * balanced, workers spread over the resources in proportion to what each
 * pays), times FOCUS_WEIGHT on the city's focus resource while it's under
 * its share.
 */
function workValue(score: number, on: number, focused: boolean): number {
  return (score / (on + 1)) * (focused ? FOCUS_WEIGHT : 1);
}

/** How many of `city`'s own workers (pinned and auto) are on `task`. */
function onTask(city: Building, counts: WorkerCounts, task: string): number {
  const n = counts.tasks.get(city.id)?.get(task);
  return n ? n.pinned + n.auto : 0;
}

/** Whether `city`'s `workTargets` ask for more workers on `task` than it has. */
function shortOf(city: Building, counts: WorkerCounts, task: string): boolean {
  return (city.workTargets?.[task] ?? 0) > onTask(city, counts, task);
}

/** Whether `city` may put another worker on a site: it has no build target, or is under it. */
function buildOpen(city: Building, counts: WorkerCounts): boolean {
  return city.workTargets?.[BUILD_TASK] === undefined || shortOf(city, counts, BUILD_TASK);
}

/**
 * The work `city` (a worker's home, or the city it would move to) offers
 * from `work`: its site if it has one, else the gathering place with the
 * best `workValue`. With a `workFocus`, the focus resource gets no bonus
 * once the worker would put its share past FOCUS_SHARE of the city's
 * gathering auto workers, and is then valued as when balanced, so a focus
 * never gives it fewer workers than balanced would.
 *
 * `own`: `city` is the worker's home, so its `workTargets` apply: the choice
 * is narrowed to the resources still short of their target (pinned and auto
 * workers counted), or to the one the worker `held` is working while that is
 * short (it stays: it is one of the workers the target asks for). With none
 * short, or none offering work, it picks as without targets. A site still
 * comes first.
 */
function pickWork(
  ctx: SimContext,
  city: Building,
  work: CityWork,
  counts: WorkerCounts,
  own: boolean,
  held?: string
): WorkChoice | null {
  // A site is built first, unless the city's build target is already met
  // (then only when there is nothing else to do).
  if (work.site && (!own || buildOpen(city, counts) || work.gather.size === 0)) return work.site;
  let pool: Iterable<WorkChoice> = work.gather.values();
  if (own && city.workTargets) {
    const wanted = [...work.gather.values()].filter((g) => shortOf(city, counts, g.res));
    const keep = wanted.filter((g) => g.res === held);
    if (wanted.length > 0) pool = keep.length > 0 ? keep : wanted;
  }
  const on = counts.byHome.get(city.id);
  const focus =
    city.workFocus !== undefined && Object.hasOwn(ctx.rules.resources, city.workFocus)
      ? city.workFocus
      : null;
  // The city's gatherers, plus the deciding worker (released before picking).
  let gatherers = 1;
  for (const n of on?.values() ?? []) gatherers += n;
  const cap = Math.max(1, Math.round(FOCUS_SHARE * gatherers));
  let best: WorkChoice | null = null;
  let bestRank: number[] = [];
  for (const g of pool) {
    const n = on?.get(g.res) ?? 0;
    const over = g.res === focus && n + 1 > cap;
    const rank = [-workValue(g.score, n, g.res === focus && !over), g.near];
    if (!best || rankBefore(rank, bestRank)) {
      best = g;
      bestRank = rank;
    }
  }
  return best;
}

/** Whether `unit` reviews its gathering work this tick: every REVIEW_TICKS, in its own phase (by id). */
function reviewDue(state: SimState, unit: Unit): boolean {
  const phase = (((mintOrder(unit.id) || 0) % REVIEW_TICKS) + REVIEW_TICKS) % REVIEW_TICKS;
  return (state.tick + phase) % REVIEW_TICKS === 0;
}

/** Lexicographic rank comparison: lower is better. */
function rankBefore(a: readonly number[], b: readonly number[]): boolean {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] < b[i];
  }
  return false;
}

function stepWorker(state: SimState, ctx: SimContext, cache: TickCache, unit: Unit): void {
  const auto = unit.auto;
  const def = ctx.rules.units[unit.type];
  if (!auto || !def) return;
  const danger = threatDistance(state, ctx, cache, unit);
  if (retreat(state, ctx, cache, unit, danger) || unit.order) return;
  const oc = ownerCache(state, ctx, cache, unit.owner);
  const { claims, counts } = workerClaims(state, ctx, cache, unit.owner, oc);
  const { shelters } = ownTerritory(state, ctx, cache, unit.owner, oc);
  const home = homeCity(state, ctx, unit);
  const count = (cell: Cell, work: WorkTarget) => {
    if (work.tier === GATHER) {
      countWorker(counts, unit, work.res, workCity(state, ctx, cache, unit, cell, home)?.id);
    }
    countTask(
      counts,
      unit,
      work.tier === GATHER ? work.res : BUILD_TASK,
      false,
      workCity(state, ctx, cache, unit, cell, home)?.id
    );
  };
  const haulTo = (cell: Cell | undefined) =>
    cell ? workCity(state, ctx, cache, unit, cell, home) : home;
  // Threatened: only work inside the territory, where the worker can't be hurt.
  const confined = danger <= SAFE_RADIUS;
  const allowed = (key: HexTileMapKey) => !confined || shelters.has(key);
  const occ = cache.derived.occupancy();
  // Free work, on a tile it can stop on (no other unit standing or heading there).
  const free = (cell: Cell) =>
    freeFor(state, ctx, cache, claims, unit, cell) &&
    stopKind(ctx.rules, occ, unit, cell, true) !== null;
  const retrying = auto.retryTick !== undefined && state.tick < auto.retryTick;
  const carry = unit.carry;

  // Sheltering in an own city while an enemy fighter is near: stay inside.
  if (confined && unit.inside && unit.inside === unloadCity(state, ctx, unit)?.id) {
    delete auto.target;
    return;
  }
  // Unloading at an own city (the economy system does it): wait there.
  // In thousandths (fixed.ts), like the load.
  const capacity = def.carryCapacity === undefined ? undefined : toMilli(def.carryCapacity);
  const full = !!carry && capacity !== undefined && carry.amount >= capacity;
  if (carry && (auto.phase === 'haul' || full || unit.inside) && unloadCity(state, ctx, unit)) {
    delete auto.target;
    return;
  }

  // The tile it hauled from, preferred when it goes back to work.
  let prefer: Cell | undefined;
  // Whether this worker already counts against its owner's searches this tick.
  let searching = false;
  if (auto.phase === 'haul') {
    if (carry) {
      if (isMoving(unit) && auto.target && cityAt(state, ctx, unit.owner, auto.target)) return;
      if (!retrying) startHaul(state, ctx, cache, unit, def.terrain, haulTo(auto.work), auto.work);
      return;
    }
    // Unloaded: back to work with a fresh search, once there's room for one.
    if (!takeSearch(cache, unit.owner)) return;
    searching = true;
    prefer = auto.work;
    delete auto.phase;
    delete auto.work;
    delete auto.target;
  } else if (carry && capacity !== undefined && !isMoving(unit)) {
    // A load goes to a city once it's full or stops filling where the
    // worker stands. A site is built first: the load waits.
    const at = workCell(state, ctx, cache, unit.owner, unit.pos);
    const here = workTarget(state, ctx, cache, unit, at);
    const held = here !== null && free(at);
    const building = held && here.tier === SITE;
    const filling = held && here.res === carry.res && carry.amount < capacity;
    if (!building && !filling) {
      const keep = held && here.res === carry.res;
      if (!keep) release(unit, claims, counts);
      startHaul(state, ctx, cache, unit, def.terrain, haulTo(at), keep ? at : undefined);
      return;
    }
  }

  // The tile the worker is working, when it's looking for better work.
  let stay: Cell | null = null;
  if (prefer) {
    // Just unloaded: look for work afresh (the tile it hauled from is worth
    // SWITCH_MARGIN more, so it goes back unless other work is clearly better).
  } else if (isMoving(unit)) {
    const target = auto.target;
    if (
      target &&
      free(target) &&
      allowed(getHexTileMapKey(target)) &&
      workTarget(state, ctx, cache, unit, target)
    ) {
      return;
    }
  } else {
    // On a grown farm's extra tile it works (and holds a slot of) the farm.
    const at = workCell(state, ctx, cache, unit.owner, unit.pos);
    const here = workTarget(state, ctx, cache, unit, at);
    if (here && free(at)) {
      // Arrived (or already somewhere useful): stay and work, unless a site
      // is waiting at home, or (now and then) gathering clearly better.
      delete auto.target;
      claim(claims, getHexTileMapKey(at), unit.id);
      count(at, here);
      if (retrying || !home || here.tier === SITE) return;
      if (
        !reviewDue(state, unit) &&
        !betterWorkWaiting(state, ctx, cache, unit, oc, claims, here.tier, home, counts)
      ) {
        return;
      }
      stay = at;
    } else if (retrying) {
      return;
    }
  }

  // Too many searches this tick already: carry on as it is until the next.
  if (!stay && !searching && !takeSearch(cache, unit.owner)) return;
  release(unit, claims, counts);
  const territory = cache.derived.owners();
  const preferKey = stay ? getHexTileMapKey(stay) : prefer ? getHexTileMapKey(prefer) : null;
  // The resource it was working, which a short `workTargets` entry keeps it on.
  const keptAt = home?.workTargets && (stay ?? prefer);
  const kept = keptAt ? workTarget(state, ctx, cache, unit, keptAt) : null;
  const held = kept?.tier === GATHER ? kept.res : undefined;

  // Best work in each own city's territory: its nearest site, and its best
  // place for each resource (by `throughput`; the tile it holds or hauled
  // from worth SWITCH_MARGIN more, a provider's place PROVIDER_PREFERENCE).
  const byCity = new Map<EntityId, CityWork>();
  const consider = (cell: Cell, key: HexTileMapKey, near: number) => {
    // Own territory first: on a big map most reachable tiles aren't.
    const cityId = territory.get(key);
    const city = cityId ? state.buildings[cityId] : undefined;
    if (!cityId || city?.owner !== unit.owner) return;
    if (stay && cityId !== home?.id) return;
    if (!oc.explored(key) || !allowed(key) || !free(cell)) return;
    const work = workTarget(state, ctx, cache, unit, cell);
    if (!work) return;
    let entry = byCity.get(cityId);
    if (work.tier === SITE) {
      if (entry?.site && entry.site.near <= near) return;
      entry ??= { gather: new Map() };
      entry.site = { cell, res: work.res, tier: SITE, score: 0, pick: 0, near };
      byCity.set(cityId, entry);
      return;
    }
    const score =
      throughput(state, ctx, cache, unit, cell, key, city) *
      (key === preferKey ? SWITCH_MARGIN : 1);
    if (score <= 0) return;
    const pick = providerAt(state, ctx, cache, unit.owner, cell, key)
      ? score * PROVIDER_PREFERENCE
      : score;
    const prev = entry?.gather.get(work.res);
    if (prev && (prev.pick > pick || (prev.pick === pick && prev.near <= near))) return;
    entry ??= { gather: new Map() };
    entry.gather.set(work.res, { cell, res: work.res, tier: GATHER, score, pick, near });
    byCity.set(cityId, entry);
  };
  const scan = (from: Reachable) => {
    for (let i = 0; i < from.order.length; i++) {
      const cell = from.order[i];
      const key = from.keys?.[i] ?? getHexTileMapKey(cell);
      consider(cell, key, from.dist.get(key) ?? 0);
    }
  };
  const area = home && inWorkArea(ctx, home, unit.pos) ? home : undefined;
  let reach: Reachable | undefined;
  if (stay && home) {
    // A review only weighs home: its tiles, by walking distance from the
    // city (the worker's own search only if it moves).
    const fromCity = cityReach(state, ctx, cache, home, def.terrain);
    for (const cell of cityTiles(cache, home.id)) {
      const key = getHexTileMapKey(cell);
      consider(cell, key, fromCity.dist.get(key) ?? Number.POSITIVE_INFINITY);
    }
  } else {
    // Round home only (its `workArea`), while the worker is there.
    reach = reachFor(state, ctx, cache, unit, def.terrain, area);
    scan(reach);
  }
  // Home first. With no work at all there, it works for the nearest own city
  // that has some (found by a search of the whole map), keeping its home: it
  // comes back for a site there at once (`betterWorkWaiting`) and for other
  // work there at its next review, so a city's own workers build its sites.
  const homeWork = home && byCity.get(home.id);
  let best = home && homeWork ? pickWork(ctx, home, homeWork, counts, true, held) : null;
  // A short target whose resource has no place in the work area may have one
  // beyond it: search wider before settling.
  oc.noPlace ??= new Set();
  const noPlace = oc.noPlace;
  const short = (task: string) =>
    home !== undefined &&
    task !== BUILD_TASK &&
    shortOf(home, counts, task) &&
    !homeWork?.gather.has(task);
  const wanting = home?.workTargets
    ? Object.keys(home.workTargets).filter(
        (task) => short(task) && !noPlace.has(`${home.id}:${task}`)
      )
    : [];
  if (!stay && area && (!best || wanting.length > 0)) {
    byCity.clear();
    reach = reachFor(state, ctx, cache, unit, def.terrain);
    scan(reach);
    // Home work reachable only by a path leaving its work area still comes first.
    const fullHomeWork = home && byCity.get(home.id);
    if (home && fullHomeWork) best = pickWork(ctx, home, fullHomeWork, counts, true, held);
    // Nothing anywhere for these this tick: later searches needn't look again.
    if (home) {
      for (const task of wanting) {
        if (!fullHomeWork?.gather.has(task)) noPlace.add(`${home.id}:${task}`);
      }
    }
  }
  if (!best && !stay) {
    if (!best) {
      let bestDist = Number.POSITIVE_INFINITY;
      let bestCity: EntityId | undefined;
      for (const cityId of [...byCity.keys()].sort((x, y) => mintOrder(x) - mintOrder(y))) {
        const d = axialHexDistance(state.buildings[cityId].pos, unit.pos);
        if (d < bestDist) {
          bestDist = d;
          bestCity = cityId;
        }
      }
      const work = bestCity && byCity.get(bestCity);
      if (bestCity && work) {
        best = pickWork(ctx, state.buildings[bestCity], work, counts, false);
      }
    }
  }

  if (stay) {
    const stayKey = getHexTileMapKey(stay);
    const here = workTarget(state, ctx, cache, unit, stay);
    const keep = () => {
      claim(claims, stayKey, unit.id);
      if (here) count(stay, here);
    };
    // Nothing clearly better: keep working here.
    if (!best || getHexTileMapKey(best.cell) === stayKey) {
      keep();
      return;
    }
    reach = reachFor(state, ctx, cache, unit, def.terrain, area);
    if (!reach.dist.has(getHexTileMapKey(best.cell))) {
      // The better work is out of reach: keep gathering here for now.
      keep();
      auto.retryTick = retryAt(state, unit, RETRY_TICKS);
      return;
    }
    // Carrying another resource: unload first, keeping the new work claimed
    // (it's preferred when the worker comes back from the city).
    if (best.tier === GATHER && carry && carry.res !== best.res) {
      startHaul(state, ctx, cache, unit, def.terrain, haulTo(stay), best.cell);
      claim(claims, getHexTileMapKey(best.cell), unit.id);
      count(best.cell, best);
      return;
    }
  }
  if (!best) {
    // Threatened outside with nothing free inside: wait inside instead.
    if (confined && !shelters.has(getHexTileMapKey(unit.pos))) {
      if (goToShelter(state, ctx, cache, unit, def.terrain, shelters)) return;
    }
    delete auto.target;
    redirect(state, unit, [unit.pos]);
    auto.retryTick = retryAt(state, unit, RETRY_TICKS);
    return;
  }
  reach ??= reachFor(state, ctx, cache, unit, def.terrain, area);
  redirect(state, unit, pathTo(reach, best.cell));
  auto.target = { q: best.cell.q, r: best.cell.r };
  delete auto.retryTick;
  claim(claims, getHexTileMapKey(best.cell), unit.id);
  count(best.cell, best);
}

/** Whether `unit` holds a pinned gather or build order (see `stepAssigned`). */
function isPinned(unit: Unit): boolean {
  const o = unit.order;
  return (o?.kind === 'gather' || o?.kind === 'build') && o.pin === true;
}

/**
 * Walks `unit` to `cell` (a pinned worker's job tile, or the city spot to
 * unload at) when it's one it can stop on and reach; else waits for the next
 * retry. Returns whether it set off.
 */
function walkTo(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  unit: Unit,
  order: { retryTick?: number },
  terrain: readonly HexTileType[],
  cell: Cell
): boolean {
  const occ = cache.derived.occupancy();
  const reach = reachFor(state, ctx, cache, unit, terrain);
  const key = getHexTileMapKey(cell);
  if (!reach.dist.has(key) || stopKind(ctx.rules, occ, unit, cell, true) === null) {
    order.retryTick = retryAt(state, unit, RETRY_TICKS);
    return false;
  }
  redirect(state, unit, pathTo(reach, cell));
  forget(occ, unit);
  addHeading(occ, unit, cell);
  delete order.retryTick;
  return true;
}

/**
 * A pinned worker (`assign`, order `pin`): not moved by automation, work
 * focus or its claims. It keeps its job until the player gives another order
 * or `automate`s it.
 *
 * - gather: walk to the target (a wild tile, or a providing building, which
 *   takes it in when it has a free slot; the economy gathers a gather order
 *   held where the worker stands, `heldGather`); once its load is full, or
 *   stops filling there, haul to a city like an auto worker (`haulSpot`,
 *   `order.haul`, the economy unloads it) and walk back. A target that ran
 *   dry or is gone, with nothing to haul, is just stood on: the order stays
 *   for the player to replace.
 * - build: walk to the site; the construction system builds it and, once
 *   done, turns the order into a gather on it (a provider) or hands the
 *   worker back to automation.
 *
 * A target it can't reach or stop on (e.g. a hidden rival building turned out
 * to stand on it) is retried every RETRY_TICKS indefinitely, bounded: the
 * player replaces the order.
 *
 * Pinned workers don't run the retreat logic of auto workers: they are under
 * manual orders, like a moved worker.
 */
function stepAssigned(state: SimState, ctx: SimContext, cache: TickCache, unit: Unit): void {
  const order = unit.order;
  const def = ctx.rules.units[unit.type];
  if (!order || !def || isMoving(unit)) return;
  const retrying =
    (order.kind === 'gather' || order.kind === 'build') &&
    order.retryTick !== undefined &&
    state.tick < order.retryTick;
  if (order.kind === 'build') {
    const site = state.buildings[order.buildingId];
    if (site && !retrying && !isPos(site, unit.pos)) {
      walkTo(state, ctx, cache, unit, order, def.terrain, site.pos);
    }
    return;
  }
  if (order.kind !== 'gather') return;
  const capacity = def.carryCapacity === undefined ? undefined : toMilli(def.carryCapacity);
  const carry = unit.carry;
  if (order.haul) {
    if (carry) {
      // The economy unloads it beside (or inside) a city.
      if (unloadCity(state, ctx, unit)) return;
      if (retrying) return;
      const home = homeCity(state, ctx, unit);
      const found = haulSpot(
        state,
        ctx,
        cache,
        unit,
        def.terrain,
        workCity(state, ctx, cache, unit, order.target, home)
      );
      if (!found) {
        order.retryTick = retryAt(state, unit, RETRY_TICKS);
        return;
      }
      const occ = cache.derived.occupancy();
      redirect(state, unit, pathTo(found.reach, found.dest));
      forget(occ, unit);
      addHeading(occ, unit, found.dest);
      delete order.retryTick;
      return;
    }
    // Unloaded: back to the job.
    delete order.haul;
    delete order.retryTick;
  }
  const full = !!carry && capacity !== undefined && carry.amount >= capacity;
  const here = unit.pos.q === order.target.q && unit.pos.r === order.target.r;
  // A load goes to a city once it's full, or stops filling at the target.
  const filling =
    here && carry && gatherResource(state, ctx, unit, cache.derived.owners()) !== null;
  if (carry && (full || (here && !filling))) {
    order.haul = true;
    delete order.retryTick;
    stepAssigned(state, ctx, cache, unit);
    return;
  }
  if (here || retrying) return;
  walkTo(state, ctx, cache, unit, order, def.terrain, order.target);
}

/** `cityId`'s workers per task, pinned and auto (the counts `pickWork` measures `workTargets` against). */
function cityTaskCounts(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  cityId: EntityId
): ReadonlyMap<string, TaskCount> {
  const owner = state.buildings[cityId]?.owner;
  if (!owner) return new Map();
  const oc = ownerCache(state, ctx, cache, owner);
  return workerClaims(state, ctx, cache, owner, oc).counts.tasks.get(cityId) ?? new Map();
}

/** A worker counted under a city's task, and the work tile it holds (see `cityWorkerPlaces`). */
interface WorkerPlace {
  unitId: EntityId;
  /** The city whose territory it works in. */
  city: EntityId;
  task: string;
  kind: keyof TaskCount;
  /** The tile it holds (a farm's own tile for any of its footprint); null when none is recorded. */
  key: HexTileMapKey | null;
}

/**
 * Every worker counted under `cityId`'s tasks (`places`: its own pinned and
 * auto ones and other cities' visitors), with the tile each holds, and the
 * city's own workers working in another city's territory (`away`). Same
 * counting as `cityTaskCounts`.
 */
function cityWorkerPlaces(
  state: SimState,
  ctx: SimContext,
  cache: TickCache,
  cityId: EntityId
): { places: WorkerPlace[]; away: WorkerPlace[] } {
  const owner = state.buildings[cityId]?.owner;
  if (!owner) return { places: [], away: [] };
  const oc = ownerCache(state, ctx, cache, owner);
  const { claims, counts } = workerClaims(state, ctx, cache, owner, oc);
  const keyOf = new Map<EntityId, HexTileMapKey>();
  for (const [key, holders] of claims) for (const id of holders) keyOf.set(id, key);
  const places: WorkerPlace[] = [];
  const away: WorkerPlace[] = [];
  for (const [unitId, t] of counts.tasked) {
    const place = { unitId, ...t, key: keyOf.get(unitId) ?? null };
    if (t.city === cityId) places.push(place);
    else if (state.units[unitId]?.homeCityId === cityId) away.push(place);
  }
  return { places, away };
}

export {
  cityTaskCounts,
  cityWorkerPlaces,
  isPinned,
  REVIEW_TICKS,
  reviewDue,
  stepAssigned,
  stepWorker,
  type TaskCount,
  type WorkerPlace,
};
